import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

/** Velden waar het schermtoetsenbord bij opent; die krijgen niet vanzelf focus bij het openen. */
const TOETSENBORD = 'textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="file"]):not([type="range"])';

/** Bij gestapelde dialogen (bv. een modal boven het menu) reageert alleen de bovenste op Tab en Escape. */
export function isTopDialog(el: HTMLElement | null) {
  const all = document.querySelectorAll('[role="dialog"]');
  return !!el && all[all.length - 1] === el;
}

/**
 * Toegankelijkheid voor modals: houdt Tab binnen het dialoogvenster, zet de focus
 * erin bij openen (nooit in een tekstveld, anders springt op mobiel het toetsenbord open; een veld
 * met `autofocus` mag dat wel) en geeft die terug aan het element dat de modal opende.
 */
export function useDialog<T extends HTMLElement = HTMLDivElement>(isOpen: boolean) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusFirst = window.setTimeout(() => {
      const el = ref.current;
      if (!el || el.contains(document.activeElement)) return;
      const eerste = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).find((n) => !n.matches(TOETSENBORD));
      (el.querySelector<HTMLElement>('[autofocus]') ?? eerste ?? el).focus();
    }, 50);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !ref.current || !isTopDialog(ref.current)) return;
      const items = Array.from<HTMLElement>(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (n: HTMLElement) => n.offsetParent !== null || n.getClientRects().length > 0,
      );
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !ref.current.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !ref.current.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusFirst);
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [isOpen]);

  return ref;
}
